import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import {
  type LookupItem,
  useGetPrintOffOptionsQuery,
  useLazyGetLookupArbitrationsQuery,
  usePrintOffContractMutation,
} from '../../store/api/saleContractApi'

// Readonly snapshot field — bg phân biệt với field nhập được (chuẩn skill
// /convert-form, đồng bộ các form CRUD).
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
  },
}

interface PrintOffDialogProps {
  open: boolean
  onClose: () => void
  contractId: number | null
  contractNo: string
  clientShort: string
  canEdit?: boolean
}

interface PrintOffForm {
  arbitration: string
  qualityDescription: string
  conditions: string
  conditionsDescription: string
}

const emptyPrintOffForm = (): PrintOffForm => ({
  arbitration: '',
  qualityDescription: '',
  conditions: '',
  conditionsDescription: '',
})

const PRINT_OFF_PREVIEW_CSS = `
@page { size: A4; margin: 0; }
.grid-report { width: 100% !important; max-width: 178mm !important; margin: 0 auto !important; }
.grid-logo { display: flex !important; justify-content: flex-start !important; align-items: center !important; height: 19mm !important; margin-bottom: 0.8mm !important; overflow: hidden !important; }
.grid-report .grid-logo img, .grid-logo img {
  width: 50mm !important;
  max-width: 50mm !important;
  max-height: 17mm !important;
  height: auto !important;
  object-fit: contain !important;
  object-position: left center !important;
}
.contract-meta { display: grid !important; grid-template-columns: auto auto !important; justify-content: center !important; column-gap: 4mm !important; row-gap: 0.8mm !important; margin: -1.5mm 0 2.2mm !important; font-size: 8pt !important; }
.contract-meta-label { font-weight: 700 !important; text-align: right !important; }
.contract-meta-value { text-align: left !important; min-width: 26mm !important; }
.contract-meta-value.bold { font-weight: 700 !important; }
.print-action { display: none !important; }
.print-grid { display: flex !important; flex-direction: column !important; gap: 1.5mm !important; margin-top: 0.6mm !important; }
.print-row { display: grid !important; grid-template-columns: 31mm minmax(0, 1fr) 24mm minmax(0, 44mm) !important; column-gap: 2.6mm !important; align-items: start !important; min-height: 4.2mm !important; }
.print-label { white-space: nowrap !important; padding-top: 0.2mm !important; }
.print-value { line-height: 1.12 !important; min-height: 3.6mm !important; white-space: pre-wrap !important; overflow-wrap: anywhere !important; }
@media print { .report-page { width: 210mm !important; max-width: none !important; min-height: 297mm !important; padding: 12mm 14mm !important; } }
`.trim()

function buildPrintOffPreviewHtml(rawHtml: string): string {
  const htmlWithoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = htmlWithoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="print-off-preview-overrides">${PRINT_OFF_PREVIEW_CSS}</style>`
  if (withBase.includes('</head>')) {
    return withBase.replace('</head>', `${styleTag}</head>`)
  }
  return `${styleTag}${withBase}`
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

export default function PrintOffDialog({ open, onClose, contractId, contractNo, canEdit = true }: PrintOffDialogProps) {
  const [form, setForm] = useState<PrintOffForm>(emptyPrintOffForm)
  const [saving, setSaving] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [previewHtml, setPreviewHtml] = useState('')
  const [printOffContract] = usePrintOffContractMutation()
  const [fetchArbitrations, { data: arbData, isFetching: arbLoading }] = useLazyGetLookupArbitrationsQuery()

  const { data: options } = useGetPrintOffOptionsQuery(contractNo, {
    skip: !contractNo || !open,
  })

  const arbItems = useMemo(() => arbData?.items ?? [], [arbData])

  useEffect(() => {
    if (open && contractId) {
      fetchArbitrations('')
    }
  }, [open, contractId, fetchArbitrations])

  useEffect(() => {
    if (options && open) {
      setForm({
        arbitration: options.arbitration ?? '',
        qualityDescription: options.qualityDescEn ?? '',
        conditions: options.conditions ?? '',
        conditionsDescription: options.conditionsDesc ?? '',
      })
    }
  }, [options, open])

  const handleSaveAndPreview = async () => {
    if (!contractId) return
    setSaving(true)
    try {
      const result = await printOffContract({
        id: contractId,
        body: {
          arbitration: form.arbitration,
          qualityDescription: form.qualityDescription,
          conditionsDescription: form.conditionsDescription,
          conditions: form.conditions,
        },
      }).unwrap()
      setPreviewHtml(buildPrintOffPreviewHtml(result.html))
      setToastMessage(result?.message ?? 'Print preview generated.')
      onClose()
    } catch {
      // keep dialog open on error
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setForm(emptyPrintOffForm())
    setSaving(false)
    onClose()
  }

  return (
    <>
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Print Offshore Contract</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Contract No" size="small" value={contractNo} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
          <Autocomplete
            size="small"
            options={arbItems}
            loading={arbLoading}
            disabled={!canEdit}
            getOptionLabel={(o: LookupItem | string) => (typeof o === 'string' ? o : o.label)}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            value={arbItems.find((x) => x.value === form.arbitration) ?? null}
            onChange={(_, v) => setForm((p) => ({ ...p, arbitration: v?.value ?? '' }))}
            onInputChange={(_, v, reason) => {
              if (reason === 'input') fetchArbitrations(v)
            }}
            filterOptions={(x) => x}
            renderInput={(params) => <TextField {...params} label="Arbitration" />}
          />
          <TextField
            label="Quality Description (EN)"
            size="small"
            multiline
            minRows={3}
            value={form.qualityDescription}
            onChange={(e) => setForm((p) => ({ ...p, qualityDescription: e.target.value }))}
            disabled={!canEdit}
          />
          <TextField label="Conditions" size="small" value={form.conditions} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
          <TextField
            label="Conditions Description"
            size="small"
            multiline
            minRows={3}
            value={form.conditionsDescription}
            onChange={(e) => setForm((p) => ({ ...p, conditionsDescription: e.target.value }))}
            disabled={!canEdit}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} size="small">
          Cancel
        </Button>
        <Box sx={{ position: 'relative' }}>
          <Button variant="contained" size="small" onClick={handleSaveAndPreview} disabled={saving}>
            {canEdit ? 'Save & Preview' : 'Preview'}
          </Button>
          {saving && (
            <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-10px', ml: '-10px' }} />
          )}
        </Box>
      </DialogActions>
    </Dialog>
    <Dialog open={Boolean(previewHtml)} onClose={() => setPreviewHtml('')} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
        <Box component="span">Print Preview - Offshore Contract</Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => setPreviewHtml('')} size="small">
            Close
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={!canEdit}
            onClick={() => {
              const printTitle = `${sanitizePdfFileNamePart(contractNo)}_Sale_Contract_Offshore.pdf`
              void printAndUploadReport({
                iframeTitle: 'Print Offshore Contract Preview',
                reportName: 'rpContractSalesLocalSOff',
                printTitle,
                html: previewHtml,
                onUploadError: setToastMessage,
              })
            }}
          >
            Print
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
        <Box
          component="iframe"
          title="Print Offshore Contract Preview"
          srcDoc={previewHtml}
          sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
        />
      </DialogContent>
    </Dialog>
    <Snackbar
      open={Boolean(toastMessage)}
      message={toastMessage}
      autoHideDuration={2000}
      onClose={() => setToastMessage('')}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    />
    </>
  )
}
